import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Paper, Stack,
  LinearProgress, IconButton, Tooltip, CircularProgress, Divider,
} from '@mui/material';
import {
  PlayArrow, CheckCircle, Cancel, RadioButtonUnchecked,
  Refresh, Speed, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';

type Category = 'Infrastructure' | 'Data' | 'Features';
type CheckStatus = 'PASS' | 'FAIL' | 'RUNNING' | null;

interface HealthCheck {
  id: string;
  name: string;
  category: Category;
  endpoint: string;
  description: string;
}

interface CheckResult {
  status: CheckStatus;
  latencyMs?: number;
  detail?: string;
  runAt?: string;
}

const CAT_COLOR: Record<Category, any> = {
  Infrastructure: 'secondary',
  Data: 'primary',
  Features: 'success',
};

const latencyColor = (ms: number) => (ms < 300 ? 'success' : ms < 1000 ? 'warning' : 'error') as any;

const CHECKS: HealthCheck[] = [
  // Infrastructure
  { id: 'qa-health', name: 'System Health', category: 'Infrastructure', endpoint: API_ENDPOINTS.QA.HEALTH, description: 'Backend health probe + DB connectivity' },

  // Data
  { id: 'studios', name: 'Studios', category: 'Data', endpoint: API_ENDPOINTS.STUDIOS.LIST, description: 'All studios list' },
  { id: 'users', name: 'Users', category: 'Data', endpoint: API_ENDPOINTS.USERS.LIST, description: 'All users' },
  { id: 'sessions', name: 'Sessions', category: 'Data', endpoint: API_ENDPOINTS.SESSIONS.LIST, description: 'Public sessions feed' },
  { id: 'bookings', name: 'Bookings', category: 'Data', endpoint: API_ENDPOINTS.BOOKINGS.LIST, description: 'All bookings' },
  { id: 'packs', name: 'Pack Definitions', category: 'Data', endpoint: API_ENDPOINTS.PACKS.LIST, description: 'Pack definitions' },
  { id: 'purchased-packs', name: 'Purchased Packs', category: 'Data', endpoint: API_ENDPOINTS.PACKS.PURCHASED_LIST, description: 'User purchased packs' },
  { id: 'classes', name: 'Classes', category: 'Data', endpoint: API_ENDPOINTS.CLASSES.LIST, description: 'Studio classes' },
  { id: 'class-types', name: 'Class Types', category: 'Data', endpoint: API_ENDPOINTS.CLASSES.ALL_TYPES, description: 'Global class type catalogue' },
  { id: 'events', name: 'Events', category: 'Data', endpoint: API_ENDPOINTS.EVENTS.LIST, description: 'All studio events' },
  { id: 'transactions', name: 'Transactions', category: 'Data', endpoint: API_ENDPOINTS.TRANSACTIONS.LIST, description: 'Payment transactions' },

  // Features
  { id: 'notifications', name: 'Notification Campaigns', category: 'Features', endpoint: API_ENDPOINTS.NOTIFICATIONS.CAMPAIGNS, description: 'Push campaign history' },
  { id: 'promo-codes', name: 'Promo Codes', category: 'Features', endpoint: API_ENDPOINTS.PROMO_CODES.LIST, description: 'Active promo codes' },
  { id: 'coupons', name: 'Coupons', category: 'Features', endpoint: API_ENDPOINTS.COUPONS.LIST, description: 'Active coupon codes' },
  { id: 'waitlist', name: 'Waitlist Stats', category: 'Features', endpoint: API_ENDPOINTS.WAITLIST.STATS, description: 'Waitlist aggregates' },
  { id: 'partner-stats', name: 'Partner API Stats', category: 'Features', endpoint: API_ENDPOINTS.PARTNER.API_STATS, description: 'Partner integration metrics' },
  { id: 'audit-logs', name: 'Audit Logs', category: 'Features', endpoint: API_ENDPOINTS.AUDIT_LOGS.LIST, description: 'Admin action history' },
];

const LS_KEY = 'qa_health_results';
function loadSaved(): Record<string, CheckResult> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}
function persist(r: Record<string, CheckResult>) {
  localStorage.setItem(LS_KEY, JSON.stringify(r));
}

const StatusIcon: React.FC<{ status: CheckStatus; size?: number }> = ({ status, size = 20 }) => {
  if (status === 'RUNNING') return <CircularProgress size={size - 4} />;
  if (status === 'PASS') return <CheckCircle sx={{ color: 'success.main', fontSize: size }} />;
  if (status === 'FAIL') return <Cancel sx={{ color: 'error.main', fontSize: size }} />;
  return <RadioButtonUnchecked sx={{ color: 'text.disabled', fontSize: size }} />;
};

const ApiHealthTab: React.FC = () => {
  const [results, setResults] = useState<Record<string, CheckResult>>(loadSaved);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [catFilter, setCatFilter] = useState<Category | 'all'>('all');
  const [collapsed, setCollapsed] = useState<Set<Category>>(new Set());

  const runCheck = useCallback(async (check: HealthCheck): Promise<'PASS' | 'FAIL'> => {
    setResults(prev => ({ ...prev, [check.id]: { status: 'RUNNING' } }));
    const start = Date.now();
    try {
      await apiService.get(check.endpoint);
      const latencyMs = Date.now() - start;
      const result: CheckResult = { status: 'PASS', latencyMs, detail: 'OK', runAt: new Date().toISOString() };
      setResults(prev => { const next = { ...prev, [check.id]: result }; persist(next); return next; });
      return 'PASS';
    } catch (e: any) {
      const latencyMs = Date.now() - start;
      const result: CheckResult = {
        status: 'FAIL',
        latencyMs,
        detail: e?.response?.data?.message || e?.message || 'Request failed',
        runAt: new Date().toISOString(),
      };
      setResults(prev => { const next = { ...prev, [check.id]: result }; persist(next); return next; });
      return 'FAIL';
    }
  }, []);

  const handleRunSingle = async (check: HealthCheck) => {
    if (runningId || batchRunning) return;
    setRunningId(check.id);
    await runCheck(check);
    setRunningId(null);
  };

  const handleRunAll = async () => {
    if (batchRunning) return;
    setBatchRunning(true);
    setBatchProgress(0);
    for (let i = 0; i < CHECKS.length; i++) {
      setRunningId(CHECKS[i].id);
      await runCheck(CHECKS[i]);
      setRunningId(null);
      setBatchProgress(i + 1);
    }
    setBatchRunning(false);
  };

  const handleClear = () => { setResults({}); localStorage.removeItem(LS_KEY); };

  const ran = CHECKS.filter(c => results[c.id]?.status && results[c.id]?.status !== 'RUNNING');
  const pass = ran.filter(c => results[c.id]?.status === 'PASS').length;
  const fail = ran.filter(c => results[c.id]?.status === 'FAIL').length;
  const latencies = CHECKS.map(c => results[c.id]?.latencyMs).filter((v): v is number => v != null);
  const avgLatency = latencies.length ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length) : 0;
  const lastRun = CHECKS.map(c => results[c.id]?.runAt).filter(Boolean).sort().pop();

  const visibleChecks = catFilter === 'all' ? CHECKS : CHECKS.filter(c => c.category === catFilter);
  const categories: Category[] = ['Infrastructure', 'Data', 'Features'];
  const grouped = categories.map(cat => ({ cat, checks: visibleChecks.filter(c => c.category === cat) })).filter(g => g.checks.length > 0);

  const toggleCollapse = (cat: Category) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  return (
    <Box sx={{ p: 2.5, overflow: 'auto', height: '100%' }}>

      {/* Summary + Run All */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>API Endpoint Health</Typography>
          <Typography variant="caption" color="text.secondary">
            {lastRun ? `Last run: ${new Date(lastRun).toLocaleTimeString()}` : 'Not yet run — click Run All'}
          </Typography>
        </Box>

        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {ran.length > 0 && (
            <>
              <Chip label={`${pass} passing`} color="success" size="small" icon={<CheckCircle sx={{ fontSize: '14px !important' }} />} />
              {fail > 0 && <Chip label={`${fail} failing`} color="error" size="small" icon={<Cancel sx={{ fontSize: '14px !important' }} />} />}
              {avgLatency > 0 && <Chip label={`avg ${avgLatency}ms`} size="small" variant="outlined" icon={<Speed sx={{ fontSize: '14px !important' }} />} />}
            </>
          )}
        </Stack>

        <Box flex={1} />

        <Button
          variant="contained"
          startIcon={batchRunning ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
          onClick={handleRunAll}
          disabled={batchRunning}
        >
          {batchRunning ? `Running ${batchProgress}/${CHECKS.length}` : `Run All (${CHECKS.length})`}
        </Button>

        <Tooltip title="Clear all results">
          <IconButton size="small" onClick={handleClear} disabled={batchRunning}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Paper>

      {batchRunning && (
        <LinearProgress
          variant="determinate"
          value={(batchProgress / CHECKS.length) * 100}
          sx={{ mb: 2, height: 6, borderRadius: 3 }}
        />
      )}

      {/* Category filter */}
      <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
        {(['all', ...categories] as const).map(cat => {
          const count = cat === 'all' ? CHECKS.length : CHECKS.filter(c => c.category === cat).length;
          const catPass = cat === 'all' ? pass : CHECKS.filter(c => c.category === cat && results[c.id]?.status === 'PASS').length;
          const catFail = cat === 'all' ? fail : CHECKS.filter(c => c.category === cat && results[c.id]?.status === 'FAIL').length;
          return (
            <Chip
              key={cat}
              label={`${cat === 'all' ? 'All' : cat} · ${catPass}✓ ${catFail > 0 ? `${catFail}✗` : ''} / ${count}`}
              onClick={() => setCatFilter(cat)}
              variant={catFilter === cat ? 'filled' : 'outlined'}
              color={cat !== 'all' && catFilter === cat ? CAT_COLOR[cat] : 'default'}
              sx={{ cursor: 'pointer', fontSize: 12 }}
            />
          );
        })}
      </Stack>

      {/* Grouped checks */}
      {grouped.map(({ cat, checks }) => {
        const isCollapsed = collapsed.has(cat);
        const catPass = checks.filter(c => results[c.id]?.status === 'PASS').length;
        const catFail = checks.filter(c => results[c.id]?.status === 'FAIL').length;
        return (
          <Box key={cat} mb={2}>
            {/* Category header */}
            <Box
              onClick={() => toggleCollapse(cat)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                cursor: 'pointer', userSelect: 'none',
                '&:hover': { opacity: 0.8 },
              }}
            >
              <Chip label={cat} size="small" color={CAT_COLOR[cat]} sx={{ fontSize: 11 }} />
              <Divider sx={{ flex: 1 }} />
              {catPass > 0 && <Typography fontSize={11} color="success.main">{catPass} ✓</Typography>}
              {catFail > 0 && <Typography fontSize={11} color="error.main">{catFail} ✗</Typography>}
              <Typography fontSize={11} color="text.disabled">{checks.length} total</Typography>
              {isCollapsed ? <ExpandMore sx={{ fontSize: 16, color: 'text.disabled' }} /> : <ExpandLess sx={{ fontSize: 16, color: 'text.disabled' }} />}
            </Box>

            {!isCollapsed && (
              <Box display="flex" flexDirection="column" gap={0.75}>
                {checks.map(check => {
                  const result = results[check.id];
                  const isRunning = runningId === check.id;
                  const isBusy = !!(runningId || batchRunning);
                  return (
                    <Paper
                      key={check.id}
                      variant="outlined"
                      sx={{
                        px: 2, py: 1.25,
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        bgcolor:
                          result?.status === 'PASS' ? '#f0faf0' :
                          result?.status === 'FAIL' ? '#fff5f5' : 'white',
                        borderColor:
                          result?.status === 'PASS' ? '#a5d6a7' :
                          result?.status === 'FAIL' ? '#ef9a9a' : '#e0e0e0',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <Box width={22} display="flex" alignItems="center" justifyContent="center">
                        <StatusIcon status={isRunning ? 'RUNNING' : result?.status ?? null} />
                      </Box>

                      <Box flex={1} minWidth={0}>
                        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                          <Typography fontSize={13} fontWeight={600}>{check.name}</Typography>
                          {result?.status === 'FAIL' && result.detail && (
                            <Typography fontSize={11} color="error.main" sx={{ maxWidth: 300 }} noWrap title={result.detail}>
                              {result.detail}
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography fontSize={11} color="text.secondary">{check.description}</Typography>
                          <Typography fontSize={10} color="text.disabled" fontFamily="monospace" noWrap sx={{ opacity: 0.7 }}>
                            {check.endpoint}
                          </Typography>
                        </Stack>
                      </Box>

                      {result?.latencyMs != null && (
                        <Chip
                          label={`${result.latencyMs}ms`}
                          size="small"
                          color={latencyColor(result.latencyMs)}
                          variant="outlined"
                          sx={{ fontSize: 11, height: 20, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}

                      {result?.runAt && (
                        <Typography fontSize={10} color="text.disabled" whiteSpace="nowrap">
                          {new Date(result.runAt).toLocaleTimeString()}
                        </Typography>
                      )}

                      <Tooltip title="Run this check">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRunSingle(check)}
                            disabled={isBusy}
                          >
                            {isRunning
                              ? <CircularProgress size={14} />
                              : <PlayArrow sx={{ fontSize: 16 }} />
                            }
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ApiHealthTab;
